import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Permission level types matching database enum
 */
export type PermissionLevel = 'none' | 'read' | 'edit';

/**
 * User role information with scope
 */
export interface UserRole {
  role_type: 'platform_superadmin' | 'brand_admin' | 'group_admin' | 'subgroup_admin' | 'user';
  brand_id: string | null;
  group_id: string | null;
  subgroup_id: string | null;
}

/**
 * Admin scope information
 */
export interface AdminScope {
  is_super_admin: boolean;
  is_admin: boolean;
  is_brand_admin: boolean;
  brand_id: string | null;
  group_id: string | null;
  subgroup_id: string | null;
  role_type: string;
}

// Type for RPC calls that aren't in generated types yet
type SupabaseRpc = typeof supabase.rpc;

/**
 * Hook to check if user has required permission for a resource
 * @param resourceId - The permission resource ID (e.g., 'content_articles')
 * @param requiredLevel - The required permission level ('read' or 'edit')
 * @returns Object with hasPermission boolean and isLoading state
 *
 * @example
 * ```tsx
 * const { hasPermission, isLoading } = usePermission('content_articles', 'edit');
 *
 * if (hasPermission) {
 *   return <CreateArticleButton />;
 * }
 * ```
 */
export function usePermission(
  resourceId: string,
  requiredLevel: PermissionLevel = 'read'
) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['permission', resourceId, requiredLevel],
    queryFn: async () => {
      // Ensure user is authenticated before checking permissions
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('Permission check skipped: no authenticated user');
        return false;
      }

      // Use type assertion for RPC that may not be in generated types yet
      const { data, error } = await (supabase.rpc as SupabaseRpc)(
        'has_permission' as any,
        {
          p_resource_id: resourceId,
          required_level: requiredLevel
        } as any
      );

      if (error) {
        console.error('Permission check error:', error);
        // Return false on error - permission denied
        return false;
      }

      return data as boolean;
    },
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Retry on failure
    retry: 2,
  });

  return {
    hasPermission: data || false,
    isLoading,
    error,
  };
}

/**
 * Hook to get user's primary role with scope information
 * @returns Object with role data and loading state
 *
 * @example
 * ```tsx
 * const { role, isLoading } = useUserRole();
 *
 * if (role?.role_type === 'brand_admin') {
 *   console.log('Brand ID:', role.brand_id);
 * }
 * ```
 */
export function useUserRole() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-role'],
    queryFn: async () => {
      // Use type assertion for RPC that may not be in generated types yet
      const { data, error } = await (supabase.rpc as SupabaseRpc)(
        'get_user_role' as any
      );

      if (error) {
        console.error('User role fetch error:', error);
        // Return null on error
        return null;
      }

      // RPC returns array, we need first item
      const results = data as unknown as UserRole[];
      return results?.[0] || null;
    },
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Retry on failure
    retry: 2,
  });

  return {
    role: data || null,
    isLoading,
    error,
  };
}

/**
 * Hook to get detailed admin scope information
 * @returns Object with admin scope data and loading state
 *
 * @example
 * ```tsx
 * const { scope, isLoading } = useAdminScope();
 *
 * if (scope?.is_super_admin) {
 *   return <SuperAdminPanel />;
 * } else if (scope?.is_brand_admin) {
 *   return <BrandAdminPanel brandId={scope.brand_id} />;
 * }
 * ```
 */
export function useAdminScope() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-scope'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_admin_scope');

      if (error) {
        console.error('Admin scope fetch error:', error);
        return null;
      }

      // RPC returns single object or array
      const result = Array.isArray(data) ? data[0] : data;
      return result as AdminScope | null;
    },
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Retry on failure
    retry: 2,
  });

  return {
    scope: data || null,
    isLoading,
    error,
  };
}

/**
 * Hook to check if user has a specific role type
 * @param roleType - The role type to check
 * @returns Object with hasRole boolean and loading state
 *
 * @example
 * ```tsx
 * const { hasRole, isLoading } = useHasRole('platform_superadmin');
 *
 * if (hasRole) {
 *   return <SuperAdminFeature />;
 * }
 * ```
 */
export function useHasRole(roleType: UserRole['role_type']) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['has-role', roleType],
    queryFn: async () => {
      // Use has_role RPC with type assertion
      const { data, error } = await (supabase.rpc as SupabaseRpc)(
        'has_role' as any,
        {
          _role: roleType,
          _user_id: (await supabase.auth.getUser()).data.user?.id || ''
        } as any
      );

      if (error) {
        console.error('Has role check error:', error);
        return false;
      }

      return data as boolean;
    },
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Retry on failure
    retry: 2,
  });

  return {
    hasRole: data || false,
    isLoading,
    error,
  };
}

/**
 * Hook to check if user is platform superadmin
 * @returns Object with isSuperAdmin boolean and loading state
 *
 * @example
 * ```tsx
 * const { isSuperAdmin } = useisPlatformSuperAdmin();
 *
 * if (isSuperAdmin) {
 *   return <PlatformSettings />;
 * }
 * ```
 */
export function useIsPlatformSuperAdmin() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['is-platform-superadmin'],
    queryFn: async () => {
      // Use type assertion for RPC that may not be in generated types yet
      const { data, error } = await (supabase.rpc as SupabaseRpc)(
        'is_platform_superadmin' as any
      );

      if (error) {
        console.error('Super admin check error:', error);
        return false;
      }

      return data as boolean;
    },
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Retry on failure
    retry: 2,
  });

  return {
    isSuperAdmin: data || false,
    isLoading,
    error,
  };
}

/**
 * Hook to check if user has any admin role
 * @returns Object with isAdmin boolean and loading state
 *
 * @example
 * ```tsx
 * const { isAdmin } = useVerifyAdminAccess();
 *
 * if (isAdmin) {
 *   return <AdminPanel />;
 * }
 * ```
 */
export function useVerifyAdminAccess() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['verify-admin-access'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('verify_admin_access');

      if (error) {
        console.error('Admin access verification error:', error);
        return false;
      }

      return data as boolean;
    },
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Retry on failure
    retry: 2,
  });

  return {
    isAdmin: data || false,
    isLoading,
    error,
  };
}
